import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Dialog,
    DialogProps,
    InternalStandardProps,
    Slide,
    Grid,
    IconButton,
    Box,
    DialogContent,
    ListItem,
    ListItemButton,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { styled } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Content, ReceivedGift } from "JS/Models";
import { AppTypography } from "JS/React/Components/AppTypography";
import { fetchBatchThumbnailUrls } from "JS/React/Hooks/Common/ImageUpload";
import { messaging } from "JS/Helpers/UserMessaging";
import { AppStandardTextFieldWithRef } from "JS/React/Components/TextFields/AppStandardTextField";
import { AppCircularProgress } from "JS/React/Components/Progress/AppCircularProgress";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList, ListOnItemsRenderedProps } from "react-window";
import { SpeakerPlaylistRowItem } from "./SpeakerPlaylistRowItem";

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const StyledDialog = styled(Dialog)(({ theme }) => ({
    boxShadow: `0px -8px 10px ${theme.palette.grey[200]}`,
    minWidth: "100%",
    minHeight: `calc(95vh - (${theme.footer.height} + ${theme.header.height}))`,
    border: `1px solid ${theme.palette.grey[200]}`,
}));

const StyledDialogWrapper = styled(Box)(({ theme }) => ({
    backgroundColor: `${theme.palette.background.default}`,
    color: `${theme.palette.common.black}`,
}));

const StyledDialogContent = styled(DialogContent)({
    padding: 0,
});

const StyledTopContent = styled(Grid)(({ theme }) => ({
    padding: theme.spacing(2),
}));

const StyledContentHeaderItem = styled(Grid)({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
});

const StyledFilterIcon = styled(FontAwesomeIcon)(({ theme }) => ({
    fontSize: "22px",
    cursor: "pointer",
    color: theme.palette.grey[600],
}));

const StyledCheckboxListWrapper = styled(Grid)(({ theme }) => ({
    overflow: "auto",
    height: `calc(70vh - (${theme.footer.height} + ${theme.header.height}))`,
    backgroundColor: theme.palette.background.default,
}));

const StyledBtnCreateWrapper = styled(Grid)({
    display: "flex",
    justifyContent: "center",
});

const StyledBtnCreate = styled(ListItemButton)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: theme.palette.primary.main,
    color: theme.colors.white,
    "&:hover": {
        backgroundColor: theme.palette.primary.main,
        color: theme.colors.white,
    },
}));

const StyledEmptyList = styled(AppTypography)(({ theme }) => ({
    marginTop: theme.spacing(3),
    color: theme.palette.grey[500],
}));

const StyledListHeight = styled(Box)({
    height: "100%",
});

export const SpeakerPlaylistDialog = (props: SpeakerPlaylistDialogProps) => {
    const {
        audios,
        handleClose,
        handleCreatePlaylist,
        onToggleSelectItem,
        selectedContentNids,
        sortType,
        playlistName,
        setPlaylistName,
        createPlaylistLoading = false,
        open,
        className,
        ...rest
    } = props;

    const inputRef = useRef(null);

    const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter") {
            const inputElement = document.activeElement as HTMLInputElement;
            if (inputElement) {
                inputElement.blur();
            }
        }
    };

    const { visibleIndices, onRender } = useItemsRendered();
    const { finalData, thumbnails } = useSpeakerPlaylist(
        audios,
        visibleIndices,
    );

    return (
        <StyledDialog
            fullWidth
            role="dialog"
            open={open}
            TransitionComponent={Transition}
            {...rest}
        >
            <StyledDialogWrapper className={className}>
                <StyledDialogContent>
                    <StyledTopContent container>
                        <StyledContentHeaderItem item xs={12}>
                            <Box
                                display={"flex"}
                                justifyContent={"flex-start"}
                                alignItems={"center"}
                            >
                                <AppTypography
                                    variant="h6"
                                    aria-label={"Create Playlist"}
                                >
                                    Create Playlist
                                </AppTypography>
                            </Box>
                            <IconButton onClick={handleClose}>
                                <StyledFilterIcon icon={["fas", "xmark"]} />
                            </IconButton>
                        </StyledContentHeaderItem>
                        <Grid
                            item
                            xs={12}
                            style={{ marginTop: "5vh", marginBottom: "2%" }}
                        >
                            <AppStandardTextFieldWithRef
                                ref={inputRef}
                                fullWidth
                                placeholder="Playlist Name"
                                value={playlistName}
                                onChange={(e) =>
                                    setPlaylistName(e.target.value)
                                }
                                onKeyDown={handleKeyPress}
                            />
                        </Grid>
                    </StyledTopContent>
                    <Grid container>
                        <StyledCheckboxListWrapper item xs={12}>
                            {finalData?.length ? (
                                <StyledListHeight>
                                    <AutoSizer>
                                        {({ height, width }) => (
                                            <FixedSizeList
                                                height={height}
                                                itemCount={
                                                    finalData?.length || 0
                                                }
                                                itemSize={140}
                                                width={width}
                                                onItemsRendered={onRender}
                                            >
                                                {({ index, style }) => (
                                                    <>
                                                        <SpeakerPlaylistRowItem
                                                            index={index}
                                                            style={style}
                                                            listItem={
                                                                finalData[index]
                                                            }
                                                            key={`${finalData[index].nid}-${index}`}
                                                            imageUrl={
                                                                thumbnails &&
                                                                thumbnails[
                                                                    finalData[
                                                                        index
                                                                    ].nid
                                                                ]
                                                            }
                                                            handleToogleCheckBox={
                                                                onToggleSelectItem
                                                            }
                                                            isCheckedItem={selectedContentNids?.includes(
                                                                finalData[index]
                                                                    .nid,
                                                            )}
                                                        />
                                                    </>
                                                )}
                                            </FixedSizeList>
                                        )}
                                    </AutoSizer>
                                </StyledListHeight>
                            ) : (
                                <StyledEmptyList align="center" variant="h6">
                                    {messaging.common.noItemsFound}
                                </StyledEmptyList>
                            )}
                        </StyledCheckboxListWrapper>
                    </Grid>
                    {selectedContentNids?.length > 0 && (
                        <Grid container>
                            <StyledBtnCreateWrapper item xs={12}>
                                <ListItem key="btnOk" disablePadding>
                                    <StyledBtnCreate
                                        onClick={
                                            !createPlaylistLoading
                                                ? handleCreatePlaylist
                                                : () => {}
                                        }
                                    >
                                        {createPlaylistLoading ? (
                                            <AppCircularProgress
                                                notPrimary={true}
                                                size={"small"}
                                            />
                                        ) : (
                                            "Create"
                                        )}
                                    </StyledBtnCreate>
                                </ListItem>
                            </StyledBtnCreateWrapper>
                        </Grid>
                    )}
                </StyledDialogContent>
            </StyledDialogWrapper>
        </StyledDialog>
    );
};

const useSpeakerPlaylist = (
    audios: (Content | ReceivedGift)[],
    visibleIndices: { start: number; end: number },
) => {
    const visibleData = useMemo(() => {
        if (!audios || !visibleIndices) return [];
        return audios.slice(visibleIndices.start, visibleIndices.end);
    }, [audios, visibleIndices]);

    const [thumbnails, setThumbnails] = useState<{
        [key: string]: string;
    }>(null);
    const debounceTimer = useRef<ReturnType<typeof setTimeout>>(null);
    const thumbnailsRef = useRef(thumbnails);
    thumbnailsRef.current = thumbnails;

    const fetchThumbnails = useCallback(() => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);

        debounceTimer.current = setTimeout(async () => {
            const currentThumbnails = thumbnailsRef.current;
            const itemsToFetch = (visibleData || [])
                .filter(
                    (d) =>
                        !currentThumbnails ||
                        !currentThumbnails[(d as Content).nid],
                )
                .map((d) => ({
                    nid: (d as Content).nid,
                    postfix: (d as Content).image_url_postfix,
                    fileName: (d as Content).image_file_name,
                }));

            if (itemsToFetch.length === 0) return;

            const newThumbnails = await fetchBatchThumbnailUrls(itemsToFetch);

            setThumbnails((prev) => ({
                ...prev,
                ...newThumbnails,
            }));
        }, 300);
    }, [visibleData]);

    useEffect(() => {
        if (audios?.length) {
            fetchThumbnails();
        }
    }, [visibleData]);

    return {
        finalData: audios,
        thumbnails,
    };
};

const useItemsRendered = () => {
    const [visibleIndices, setVisibleIndices] = useState<{
        start: number;
        end: number;
    }>();

    const onRender = (props: ListOnItemsRenderedProps) => {
        setVisibleIndices({
            start: props.visibleStartIndex,
            end: props.visibleStopIndex + 1,
        });
    };

    return {
        visibleIndices,
        onRender,
    };
};

export interface SpeakerPlaylistDialogProps
    extends InternalStandardProps<DialogProps> {
    audios: (Content | ReceivedGift)[];
    handleClose: () => void;
    handleCreatePlaylist: () => void;
    onToggleSelectItem: (nid: string, checked: boolean) => void;
    selectedContentNids: string[];
    sortType?: string;
    setPlaylistName: React.Dispatch<React.SetStateAction<string>>;
    playlistName: string;
    createPlaylistLoading?: boolean;
}
